import PropTypes from 'prop-types';

import { IconCheckbox, IconChipRemove } from '../../../Components/Common/Icons';
import { useLanguage } from '../../../localization/useLanguage';
import ContactAvatar from '../../../Components/WebChat/Common/ContactList/ContactAvatar';
import { CONTACT_LOAD_THRESHOLD } from '../constants';
import { formatContactPhone, getContactName } from '../../../Components/WebChat/Common/ContactList/contactHelpers';
import dialogStyles from '../../../Components/WebChat/Common/ContactList/dialog.module.scss';
import styles from './index.module.scss';

/**
 * Scrollable contact list; each row is a checkbox-role button that toggles on click.
 * A hidden native checkbox behind a label missed real mouse clicks in some browsers.
 *
 * @param {object[]} contacts Loaded contacts, A→Z.
 * @param {Map} selected Selected contacts keyed by userId.
 * @param {Function} onToggle Called with the contact whose row was clicked.
 * @param {Function} onLoadMore Asks for the next page once 70% of the list is scrolled.
 * @param {boolean} isEmpty Shows E11 instead of the list.
 */
const ContactPicker = ({ contacts, selected, onToggle, onLoadMore, isEmpty }) => {
    const { t } = useLanguage();

    const handleScroll = event => {
        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        if (scrollTop + clientHeight >= scrollHeight * CONTACT_LOAD_THRESHOLD) onLoadMore();
    };

    if (isEmpty) return <p className={dialogStyles.emptyNote}>{t('chat.EMPTY_NO_DATA')}</p>;

    return (
        <div className={styles.list} onScroll={handleScroll}>
            {contacts.map(contact => {
                const name = getContactName(contact);
                return (
                    <button
                        key={contact.userId}
                        type="button"
                        role="checkbox"
                        aria-checked={selected.has(contact.userId)}
                        aria-label={t('createChat.SELECT_CONTACT', { name })}
                        className={styles.row}
                        onClick={() => onToggle(contact)}
                    >
                        <span className={styles.checkbox} aria-hidden="true">
                            <IconCheckbox />
                        </span>
                        <ContactAvatar contact={contact} />
                        <span className={styles.info}>
                            <span className={styles.name} title={name}>
                                {name}
                            </span>
                            <span className={styles.meta}>{formatContactPhone(contact)}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

ContactPicker.propTypes = {
    contacts: PropTypes.arrayOf(PropTypes.object).isRequired,
    selected: PropTypes.instanceOf(Map).isRequired,
    onToggle: PropTypes.func.isRequired,
    onLoadMore: PropTypes.func.isRequired,
    isEmpty: PropTypes.bool,
};

/**
 * One chip per selected contact, wrapping onto several lines; the x deselects it.
 *
 * @param {Map} selected Selected contacts keyed by userId.
 * @param {Function} onRemove Called with the contact whose chip was dismissed.
 */
export const SelectionChips = ({ selected, onRemove }) => {
    const { t } = useLanguage();
    if (selected.size === 0) return null;

    return (
        <div className={styles.chips}>
            {[...selected.values()].map(contact => {
                const name = getContactName(contact);
                return (
                    <span key={contact.userId} className={styles.chip}>
                        <ContactAvatar contact={contact} size="sm" />
                        <span className={styles.chipName} title={name}>
                            {name}
                        </span>
                        <button
                            type="button"
                            className={styles.chipRemove}
                            onClick={() => onRemove(contact)}
                            aria-label={t('createChat.REMOVE_MEMBER', { name })}
                        >
                            <IconChipRemove />
                        </button>
                    </span>
                );
            })}
        </div>
    );
};

SelectionChips.propTypes = {
    selected: PropTypes.instanceOf(Map).isRequired,
    onRemove: PropTypes.func.isRequired,
};

export default ContactPicker;
